import {
  useCadModelObjectLimitedBoundingBox,
  useObjectBoundingBox,
} from "@/hooks/use-object-bounding-box";
import { CadModelObject, PointCloudObject } from "@/object-cache";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { SceneFilter, usesCadModel } from "@/types/scene-filter";
import { selectPanoCameraTransform } from "@/utils/camera-transform";
import { getSheetCenter } from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  IElementImg360,
} from "@faro-lotv/ielement-types";
import { CachedWorldTransform } from "@faro-lotv/project-source";
import { useMemo, useRef } from "react";
import { Box3, Matrix4, Object3D, Quaternion, Vector3 } from "three";
import { ModeWithSceneFilterInitialState } from "../mode";

/** Half-size of the framing box if there's only one pano or all panos are really close together*/
const BOUNDING_BOX_SIZE = 30;

/**
 *
 * @param panos A set of panorama pictures
 * @param sheet Reference sheet to compute pano pose
 * @returns The bounding box containing all panos, or undefined if not panos are provided
 */
export function usePanosBoundingBox(
  panos: IElementImg360[],
  sheet: IElementGenericImgSheet | undefined,
): Box3 | undefined {
  const store = useAppStore();
  return useMemo(() => {
    const state = store.getState();
    return computePositionsBoundingBox(
      panos.map((pano) =>
        new Vector3().fromArray(
          selectPanoCameraTransform(pano, sheet)(state).position,
        ),
      ),
    );
  }, [panos, sheet, store]);
}

/**
 * @param positions Capture positions, e.g. of panos or scans
 * @returns The box overview mode frames around the positions, or undefined if there are none
 */
export function computePositionsBoundingBox(
  positions: Vector3[],
): Box3 | undefined {
  const box = new Box3().setFromPoints(positions);
  if (box.isEmpty()) return;

  const TEMP_VEC3 = new Vector3();
  const size = box.getSize(TEMP_VEC3);
  if (size.x < Number.EPSILON || size.z < Number.EPSILON) {
    const center = box.getCenter(TEMP_VEC3);
    box.min.copy(center).subScalar(BOUNDING_BOX_SIZE);
    box.max.copy(center).addScalar(BOUNDING_BOX_SIZE);
  }
  return box;
}

/**
 * @param pointCloud in the scene
 * @param cad in the scene
 * @returns the bounding box containing the pointCloud and or cad depending on the current user selected scene
 */
export function useCurrentModelBoundingBox(
  pointCloud: PointCloudObject | null,
  cad: CadModelObject | null,
): Box3 | undefined {
  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  const cloudSelected =
    sceneFilter === SceneFilter.PointCloud ||
    sceneFilter === SceneFilter.Overlay;
  const cadSelected = usesCadModel(sceneFilter);

  const cloudBox = useObjectBoundingBox(pointCloud, pointCloud?.iElement.id);
  const cadBox = useCadModelObjectLimitedBoundingBox(cad, cad?.iElement.id);

  return useMemo(() => {
    let box: Box3 | undefined;
    if (cloudSelected && cloudBox) {
      box = cloudBox.clone();
    }
    if (cadSelected && cadBox.boundingBox) {
      if (box) {
        box.union(cadBox.boundingBox);
      } else {
        box = cadBox.boundingBox.clone();
      }
    }
    return box;
  }, [cadBox, cadSelected, cloudBox, cloudSelected]);
}

/**
 * @param modelBox bounding box of the current active scene models
 * @param sheet rendered in the current scene
 * @param sheetTransform from the data model
 * @param waitCadLoading true to use initial state (if available) while waiting for cad model loading
 * @param initialState defined by the deep link
 * @returns the best pivot to use to look at the scene
 */
export function useOverviewTarget(
  modelBox: Box3 | undefined,
  sheet: IElementGenericImgSheet | undefined,
  sheetTransform: CachedWorldTransform,
  waitCadLoading: boolean,
  initialState?: ModeWithSceneFilterInitialState,
): Vector3 | undefined {
  // Extract the target from the initialState if defined
  const initialTarget = useRef<Vector3 | undefined>(
    initialState && initialState.camera
      ? new Vector3().fromArray(initialState.camera.target)
      : undefined,
  );

  return useMemo(() => {
    // If we have a target from the initialState that overrides the first target
    if (initialTarget.current) {
      const target = initialTarget.current;
      if (!waitCadLoading) initialTarget.current = undefined;
      return target;
    }
    // If we have a valid model use the center of the bounding box of the model
    if (modelBox) {
      return modelBox.getCenter(new Vector3());
    }
    if (sheet) {
      // Fallback to the center of the sheet
      return getSheetCenter(
        sheet,
        new Matrix4().fromArray(sheetTransform.worldMatrix),
      );
    }
  }, [modelBox, sheet, sheetTransform.worldMatrix, waitCadLoading]);
}

/**
 * Position of the camera when the overview mode frames a scene: raised above the pivot and pulled back
 * horizontally, both by the given distance
 *
 * @param target The rotation pivot to look at
 * @param distance How far to move the camera away from the pivot, usually half the diagonal of the scene
 * @returns the camera position
 */
export function computeOverviewCameraPosition(
  target: Vector3,
  distance: number,
): Vector3 {
  const dir = new Vector3(target.x, 0, target.z)
    .normalize()
    .multiplyScalar(distance);

  return new Vector3(
    target.x - dir.x,
    target.y - dir.y + distance,
    target.z - dir.z,
  );
}

/**
 * @param position of the camera
 * @param target to center
 * @returns the quaternion to rotate the camera toward the target
 */
export function computeLookAtQuaternion(
  position: Vector3,
  target: Vector3,
): Quaternion {
  return new Quaternion().setFromRotationMatrix(
    new Matrix4().lookAt(position, target, Object3D.DEFAULT_UP),
  );
}
